import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { onAuthChange } from '../lib/auth';
import { setTier } from '../lib/license';
import { refreshTrialStatus } from '../lib/trialClient';
import { bindPendingCode } from '../lib/bindPendingCode';
import { setLanguage, SUPPORTED_CODES, LANGUAGE_STORAGE_KEY } from '../lib/i18n';

const API = import.meta.env.VITE_API_URL || 'https://api.scrivar.com';

// On sign-in, prefer the server-stored language over the local detection —
// the account-level choice follows the user across devices. Stored locally
// so the next cold boot picks it up before the server roundtrip.
function applyServerLanguage(code) {
  if (!code || !SUPPORTED_CODES.includes(code)) return;
  const current = (() => {
    try { return localStorage.getItem(LANGUAGE_STORAGE_KEY); } catch { return null; }
  })();
  if (current === code) return;
  setLanguage(code);
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);

  /**
   * Re-fetch /account and refresh the cached account row.
   * Pass `{ forceTokenRefresh: true }` after operations that change something
   * a Firebase claim depends on (e.g. email verification — the cached ID
   * token still says email_verified=false until the next refresh, which
   * means server-side authMiddleware's upsert keeps writing 0 to the DB).
   */
  const refreshAccount = useCallback(async ({ forceTokenRefresh = false } = {}) => {
    const firebaseUser = (await import('../lib/firebase')).auth.currentUser;
    if (!firebaseUser) return null;
    try {
      const token = await firebaseUser.getIdToken(forceTokenRefresh);
      const res = await fetch(`${API}/account`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return null;
      const data = await res.json();
      setAccount(data);
      if (data.tier) setTier(data.tier);
      if (data.preferredLanguage) applyServerLanguage(data.preferredLanguage);
      const cached = JSON.parse(localStorage.getItem('scrivar_session') || 'null') || {};
      localStorage.setItem('scrivar_session', JSON.stringify({ ...cached, ...data }));
      return data;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    // On mount, pre-warm localStorage from the Electron store so the rest of
    // the app sees a valid cached session before Firebase reattaches.
    if (!localStorage.getItem('scrivar_session') && window.electronAPI?.getSession) {
      window.electronAPI.getSession()
        .then((stored) => {
          if (stored) localStorage.setItem('scrivar_session', JSON.stringify(stored));
        })
        .catch(() => {});
    }

    const unsub = onAuthChange(async (firebaseUser) => {
      setUser(firebaseUser);

      // Desktop-only: any successful auth state means the user is past
      // the first-run Welcome screen. Flip the flag in electron-store so
      // future cold starts go straight to /tools instead of /welcome.
      // (Welcome.jsx ALSO does this for the case where a signed-in user
      // lands on /welcome directly, but doing it here too means we flip
      // the flag even if the user never sees Welcome at all — e.g. they
      // visited the Login page via a deep-link.)
      if (firebaseUser && window.electronAPI?.markFirstRunComplete) {
        window.electronAPI.markFirstRunComplete().catch(() => { /* non-fatal */ });
      }

      if (firebaseUser) {
        const cached = JSON.parse(localStorage.getItem('scrivar_session') || 'null');
        if (cached) {
          setAccount(cached);
          if (cached.tier) setTier(cached.tier);
        }

        try {
          const token = await firebaseUser.getIdToken();
          const res = await fetch(`${API}/account`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            const data = await res.json();
            setAccount(data);
            if (data.tier) setTier(data.tier);
            if (data.preferredLanguage) applyServerLanguage(data.preferredLanguage);
            const merged = { ...(cached || {}), ...data };
            localStorage.setItem('scrivar_session', JSON.stringify(merged));
          }
          // Bind a code stashed at signup, once verified. Covers the new-tab
          // reattach (email link opened a fresh tab) and fresh sign-in paths.
          // No-op when there's no pending code. On success, re-fetch /account
          // so the tier reflects the newly-bound license.
          const { bound } = await bindPendingCode();
          if (bound) await refreshAccount({ forceTokenRefresh: true });
          await refreshTrialStatus(token);
        } catch {
          // Offline — keep cached tier
        }
      } else {
        // Firebase has no live user. This is EITHER an explicit sign-out
        // (auth.signOut already cleared scrivar_session) OR an offline cold
        // start where Firebase couldn't restore the session (token refresh
        // needs network). We must NOT wipe scrivar_session here — it's the
        // offline license proof for a signed-in lifetime/paid user, and the
        // hard gate honors it (authGate.hasCachedSession). Clearing stays the
        // job of auth.signOut (explicit logout) and licenseCheck (online kick).
        const cached = JSON.parse(localStorage.getItem('scrivar_session') || 'null');
        if (cached) {
          // Surface the cached account/tier so the app works offline.
          setAccount(cached);
          if (cached.tier) setTier(cached.tier);
        } else {
          setAccount(null);
        }
      }

      setLoading(false);
    });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only auth subscription; refreshAccount is a stable useCallback
  }, []);

  // Backstop bind: a code stashed at signup also binds whenever `user` changes
  // (sign-in / session reattach) AND when the window regains focus (the user
  // came back from the verification link in another tab/app). bindPendingCode
  // is a cheap no-op when there's no pending code, so polling on focus is fine.
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    const tryBind = async () => {
      const { bound } = await bindPendingCode();
      if (!cancelled && bound) await refreshAccount({ forceTokenRefresh: true });
    };
    tryBind();
    window.addEventListener('focus', tryBind);
    return () => { cancelled = true; window.removeEventListener('focus', tryBind); };
  }, [user, refreshAccount]);

  return (
    <AuthContext.Provider value={{ user, account, loading, setAccount, refreshAccount }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  return ctx || { user: null, account: null, loading: false, setAccount: () => {}, refreshAccount: async () => null };
}
